import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { fetchDoseSuggestion } from '../../../../../api/doseSuggestions'
import { postWeightVital } from '../../../../../api/vitals'
import { queryClient } from '../../../../../store/provider/TanStackProvider'
import { DoseSuggestion, needsStrength } from '../../../../../types/doseSuggestion.type'

export type ReferenceDoseInput = {
  drug?: string
  form?: string
  strength?: string
  patientId?: string
  encounterId?: string
}

export type ReferenceDoseState = {
  /** Enough is known (drug, form, strength when the form needs one, patient) to look a dose up. */
  enabled: boolean
  suggestion: DoseSuggestion | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  /** Record the patient's weight (kg) as a vital, then look the dose up again. */
  saveWeight: (kg: number) => Promise<void>
  isSavingWeight: boolean
  saveWeightError: boolean
}

const clean = (value: string | undefined) => (value ?? '').trim()

/**
 * MedKB reference dose for the order form's current drug/form/strength and the encounter's patient.
 * Nothing is prefilled from here: the panel shows it and the prescriber chooses to use it.
 */
export const useReferenceDose = ({ drug, form, strength, patientId, encounterId }: ReferenceDoseInput) => {
  const drugName = clean(drug)
  const formName = clean(form)
  const strengthName = clean(strength)
  const patient = clean(patientId)
  const enabled = !!drugName && !!formName && !!patient && (!needsStrength(formName) || !!strengthName)

  const query = useQuery(
    {
      enabled,
      queryFn: () =>
        fetchDoseSuggestion({
          drug: drugName,
          form: formName,
          patientId: patient,
          strength: needsStrength(formName) ? strengthName : undefined
        }),
      queryKey: ['dose-suggestion', drugName, formName, needsStrength(formName) ? strengthName : '', patient],
      retry: false,
      staleTime: 5 * 60 * 1000
    },
    queryClient
  )

  const weight = useMutation(
    {
      mutationFn: (kg: number) =>
        postWeightVital({ encounterId: clean(encounterId) || undefined, kg, patientId: patient }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['vital_signs'] })
        await query.refetch()
      }
    },
    queryClient
  )

  const refetch = useCallback(() => {
    void query.refetch()
  }, [query])

  const saveWeight = useCallback(
    async (kg: number) => {
      await weight.mutateAsync(kg)
    },
    [weight]
  )

  const state: ReferenceDoseState = {
    enabled,
    isError: query.isError,
    isLoading: enabled && query.isPending,
    isSavingWeight: weight.isPending,
    refetch,
    saveWeight,
    saveWeightError: weight.isError,
    suggestion: enabled ? query.data : undefined
  }
  return state
}
